/* CamGuard wishlist, theme-native (08.10.2026).
 * Saved in this browser only (localStorage key cgau-wishlist-v1): product IDs and handles, nothing else.
 * No cookies, no customer data, nothing sent anywhere except Shopify's own storefront routes.
 * Gordon's picks and the reasons: ~/Downloads/CamGuard Wishlist - 08.10.2026/Wishlist options - 08.10.2026.md
 */
if (!window.CgauWishlist) {
  const KEY = 'cgau-wishlist-v1';
  const MAX = 60;
  const ITEM_SECTION = 'cgau-wishlist-item';
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/i;
  const ID = /^[1-9]\d{0,19}$/;
  let memory = [];

  const clean = (list) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : [])
      .filter((item) => item && ID.test(String(item.id)) && HANDLE.test(String(item.handle)))
      .map((item) => ({ id: String(item.id), handle: String(item.handle).toLowerCase(), t: Number(item.t) || 0 }))
      .filter((item) => !seen.has(item.id) && seen.add(item.id))
      .slice(0, MAX);
  };
  const read = () => {
    try { return clean(JSON.parse(localStorage.getItem(KEY) || '[]')); }
    catch (_) { return clean(memory); }
  };
  const write = (list, silent = false) => {
    memory = clean(list);
    try { localStorage.setItem(KEY, JSON.stringify(memory)); }
    catch (_) { /* Storage blocked (private mode): the list still works on this page. */ }
    paint();
    if (!silent) document.dispatchEvent(new CustomEvent('cgau:wishlist', { detail: { count: memory.length } }));
  };
  const has = (id) => read().some((item) => item.id === String(id));
  const root = () => (window.Shopify?.routes?.root || '/');
  const pageUrl = () => document.querySelector('[data-cgau-wish-header]')?.getAttribute('href') || `${root()}pages/wishlist`;

  /* Clarity: same rules as cgau-clarity-funnel.js (consent given, live theme only, never in previews). */
  const clarity = (name) => {
    try {
      const theme = window.Shopify?.theme?.role;
      if (window.Shopify?.designMode || ['unpublished', 'development'].includes(theme) || document.documentElement.dataset.cgauQa === 'true') return;
      const privacy = window.Shopify?.customerPrivacy;
      if (privacy?.analyticsProcessingAllowed?.() !== true || privacy?.marketingAllowed?.() !== true) return;
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (_) { /* Analytics never blocks the wishlist. */ }
  };

  let toastTimer = 0;
  const toast = (text, link) => {
    let box = document.querySelector('.cgau-wish-toast');
    if (!box) {
      box = document.createElement('div');
      box.className = 'cgau-wish-toast';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      document.body.append(box);
    }
    box.replaceChildren(document.createTextNode(text));
    if (link) {
      const a = document.createElement(link.href ? 'a' : 'button');
      if (link.href) a.href = link.href; else { a.type = 'button'; a.addEventListener('click', link.onClick); }
      a.className = 'cgau-wish-toast__link';
      a.textContent = link.text;
      box.append(document.createTextNode(' · '), a);
    }
    box.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => box.classList.remove('is-visible'), 3200);
  };

  function paint() {
    const list = read();
    const ids = new Set(list.map((item) => item.id));
    document.querySelectorAll('[data-cgau-wish]').forEach((button) => {
      const saved = ids.has(button.dataset.id);
      const title = button.dataset.title || 'this product';
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? `Remove ${title} from wishlist` : `Save ${title} to wishlist`);
      button.title = saved ? 'Saved to your wishlist' : 'Save to wishlist';
    });
    document.querySelectorAll('[data-cgau-wish-header]').forEach((link) => {
      link.setAttribute('aria-label', list.length ? `Wishlist, ${list.length} saved` : 'Wishlist');
      const count = link.querySelector('[data-cgau-wish-count]');
      if (!count) return;
      count.textContent = list.length > 99 ? '99+' : String(list.length);
      count.hidden = list.length === 0;
    });
  }

  function toggle(button) {
    const id = button.dataset.id, handle = button.dataset.handle;
    if (!ID.test(String(id)) || !HANDLE.test(String(handle))) return;
    const list = read();
    if (list.some((item) => item.id === id)) {
      write(list.filter((item) => item.id !== id));
      clarity('cgau_wishlist_remove');
      toast('Removed from your wishlist');
      return;
    }
    if (list.length >= MAX) list.pop();
    write([{ id, handle, t: Date.now() }, ...list]);
    clarity('cgau_wishlist_add');
    const onPage = document.querySelector('[data-cgau-wish-page]');
    toast('Saved to your wishlist', onPage ? null : { text: 'View', href: pageUrl() });
    button.classList.remove('is-popping'); void button.offsetWidth; button.classList.add('is-popping');
  }

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cgau-wish]') : null;
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggle(button);
  });
  window.addEventListener('storage', (event) => { if (event.key === KEY) { paint(); page?.refresh(); } });
  window.addEventListener('pageshow', (event) => { if (event.persisted) { paint(); page?.refresh(); } });
  new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-cgau-wish]') || n.querySelector?.('[data-cgau-wish]'))))) paint();
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* Cart adds announce themselves the way product-form.js does, so the cart icon, drawer and Clarity funnel see them. */
  async function addToCart(lines) {
    let events = null;
    try { events = await import('@shopify/events'); } catch (_) { events = null; }
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = events?.CartLinesUpdateEvent?.createPromise?.();
    if (deferred) {
      document.dispatchEvent(new events.CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: 1 })),
        promise: deferred.promise,
      }));
    }
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: lines.map((line) => ({ id: Number(line.id), quantity: 1 })), sections: sectionIds.join(',') }),
    });
    const result = await response.json().catch(() => ({}));
    const cart = await fetch(`${root()}cart.js`, { credentials: 'same-origin' }).then((r) => r.json()).catch(() => null);
    const didError = !response.ok || Boolean(result.status);
    if (deferred && cart) {
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cgau-wishlist', sourceId: 'cgau-wishlist', itemCount: lines.length, sections: result.sections, didError },
      });
    } else if (cart) {
      document.querySelector('cart-icon')?.renderCartBubble?.(cart.item_count);
    }
    if (didError) throw new Error(result.description || result.message || 'Could not add to cart');
    return cart;
  }
  const openCart = () => {
    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) trigger.click(); else location.href = `${root()}cart`;
  };

  /* The wishlist page: renders each saved product through the cgau-wishlist-item section. */
  let page = null;
  const container = document.querySelector('[data-cgau-wish-page]');
  if (container) page = createPage(container);

  function createPage(el) {
    const grid = el.querySelector('[data-cgau-wish-grid]');
    const empty = el.querySelector('[data-cgau-wish-empty]');
    const toolbar = el.querySelector('[data-cgau-wish-toolbar]');
    const status = el.querySelector('[data-cgau-wish-status]');
    const countEl = el.querySelector('[data-cgau-wish-page-count]');
    const compareBtn = el.querySelector('[data-cgau-wish-compare]');
    const addAllBtn = el.querySelector('[data-cgau-wish-addall]');
    const cache = new Map();
    let version = 0;

    const fetchItem = async (handle) => {
      if (cache.has(handle)) return cache.get(handle);
      const request = fetch(`${root()}products/${encodeURIComponent(handle)}?section_id=${ITEM_SECTION}`, { credentials: 'same-origin' })
        .then(async (response) => {
          if (!response.ok) return null;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          return doc.querySelector('[data-cgau-wish-item]');
        })
        .catch(() => undefined);
      cache.set(handle, request);
      return request;
    };
    const pool = async (items, size, worker) => {
      const results = new Array(items.length); let next = 0;
      await Promise.all(Array.from({ length: Math.min(size, items.length) }, async () => {
        while (next < items.length) { const i = next++; results[i] = await worker(items[i]); }
      }));
      return results;
    };
    const setStatus = (text) => { if (status) status.textContent = text; };
    const updateTools = () => {
      const cards = [...grid.querySelectorAll('[data-cgau-wish-item]')];
      const addable = cards.filter((card) => card.dataset.canAdd === 'true');
      if (countEl) countEl.textContent = `${cards.length} saved`;
      if (toolbar) toolbar.hidden = cards.length === 0;
      if (empty) empty.hidden = cards.length !== 0;
      if (addAllBtn) { addAllBtn.disabled = addable.length === 0; addAllBtn.textContent = addable.length > 1 ? `Add all to cart (${addable.length})` : 'Add all to cart'; }
      const comparison = document.querySelector('cgau-product-comparison');
      if (compareBtn) {
        compareBtn.hidden = !comparison || cards.length < 2;
        compareBtn.textContent = cards.length > 3 ? 'Compare first 3' : 'Compare these';
      }
    };

    async function refresh() {
      const run = ++version;
      let list = read();
      grid.setAttribute('aria-busy', 'true');
      if (list.length) setStatus('Loading your saved products…');
      const cards = await pool(list, 4, (item) => fetchItem(item.handle));
      if (run !== version) return;
      const keep = [];
      let dropped = 0, failed = 0;
      const fragment = document.createDocumentFragment();
      cards.forEach((card, i) => {
        const item = list[i];
        if (card === undefined) { keep.push(item); failed++; cache.delete(item.handle); return; } // network error: keep it, try again later
        if (!card || card.dataset.id !== item.id) { dropped++; return; } // deleted, unpublished or handle reused
        keep.push({ ...item, handle: card.dataset.handle || item.handle });
        fragment.append(document.importNode(card, true));
      });
      grid.replaceChildren(fragment);
      grid.setAttribute('aria-busy', 'false');
      if (dropped || keep.some((item, i) => item.handle !== list[i]?.handle)) write(keep, true);
      const notes = [];
      if (dropped) notes.push(`${dropped} saved product${dropped > 1 ? 's are' : ' is'} no longer on the site, so ${dropped > 1 ? 'they were' : 'it was'} removed.`);
      if (failed) notes.push(`${failed} saved product${failed > 1 ? 's' : ''} could not load. Refresh the page to try again.`);
      setStatus(notes.join(' '));
      updateTools();
      paint();
    }

    async function importFromLink() {
      const params = new URLSearchParams(location.search);
      const raw = params.get('items');
      if (raw === null) return;
      const handles = [...new Set(raw.split(',').map((h) => h.trim().toLowerCase()).filter((h) => HANDLE.test(h)))].slice(0, MAX);
      params.delete('items');
      history.replaceState(history.state, '', `${location.pathname}${params.toString() ? `?${params}` : ''}${location.hash}`);
      if (!handles.length) return;
      setStatus('Opening your shared list…');
      const list = read();
      const known = new Set(list.map((item) => item.handle));
      const cards = await pool(handles.filter((h) => !known.has(h)), 4, (h) => fetchItem(h));
      const added = cards.filter(Boolean).map((card, i) => ({ id: card.dataset.id, handle: card.dataset.handle, t: Date.now() - i }));
      if (added.length) { write([...added, ...list], true); toast(`Added ${added.length} product${added.length > 1 ? 's' : ''} from your link`); }
    }

    el.addEventListener('click', async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const removeBtn = target.closest('[data-cgau-wish-remove]');
      if (removeBtn) {
        const card = removeBtn.closest('[data-cgau-wish-item]');
        const id = card?.dataset.id;
        card?.remove();
        write(read().filter((item) => item.id !== id), true);
        updateTools();
        clarity('cgau_wishlist_remove');
        toast('Removed from your wishlist');
        return;
      }
      const addBtn = target.closest('[data-cgau-wish-add]');
      if (addBtn) {
        const card = addBtn.closest('[data-cgau-wish-item]');
        addBtn.disabled = true; const label = addBtn.textContent; addBtn.textContent = 'Adding…';
        try { await addToCart([{ id: card.dataset.variantId }]); toast('Added to cart', { text: 'View cart', onClick: openCart }); addBtn.textContent = 'Added ✓'; }
        catch (error) { toast(error.message); addBtn.textContent = label; }
        setTimeout(() => { addBtn.disabled = false; addBtn.textContent = label; }, 2500);
        return;
      }
      if (target.closest('[data-cgau-wish-addall]')) {
        const lines = [...grid.querySelectorAll('[data-cgau-wish-item][data-can-add="true"]')].map((card) => ({ id: card.dataset.variantId }));
        if (!lines.length) return;
        addAllBtn.disabled = true;
        try { await addToCart(lines); toast(`Added ${lines.length} item${lines.length > 1 ? 's' : ''} to cart`, { text: 'View cart', onClick: openCart }); }
        catch (error) { toast(error.message); }
        addAllBtn.disabled = false;
        return;
      }
      if (target.closest('[data-cgau-wish-copy]')) {
        const url = new URL(pageUrl(), location.origin);
        url.searchParams.set('items', read().map((item) => item.handle).join(','));
        try { await navigator.clipboard.writeText(url.toString()); toast('Link copied. Open it on any phone or computer to see this list.'); }
        catch (_) { window.prompt('Copy this link to open your wishlist on another device:', url.toString()); }
        return;
      }
      if (target.closest('[data-cgau-wish-compare]')) {
        const comparison = document.querySelector('cgau-product-comparison');
        const cards = [...grid.querySelectorAll('[data-cgau-wish-item]')].slice(0, 3);
        if (!comparison || cards.length < 2 || typeof comparison.openComparison !== 'function') return;
        comparison.selected = new Map(cards.map((card) => [card.dataset.id, {
          id: card.dataset.id, url: `/products/${card.dataset.handle}`, handle: card.dataset.handle, title: card.dataset.title || 'Saved product',
        }]));
        comparison.persist?.(); comparison.sync?.();
        void comparison.openComparison();
      }
    });
    document.addEventListener('cgau:wishlist', () => {
      const ids = new Set(read().map((item) => item.id));
      const shown = [...grid.querySelectorAll('[data-cgau-wish-item]')].map((card) => card.dataset.id);
      if (shown.length !== ids.size || shown.some((id) => !ids.has(id))) refresh();
    });

    importFromLink().finally(refresh);
    return { refresh };
  }

  window.CgauWishlist = Object.freeze({ version: 1, read, has, refresh: () => page?.refresh() });
  paint();
}
